import {
  App,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Typography,
  theme as antdTheme,
} from 'antd';
import {
  ArrowRightOutlined,
  BookOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileTextOutlined,
  IdcardOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useCommandPaletteStore } from '@/store/command-palette';
import { useAuthStore } from '@/store/auth';
import { useRecentPagesStore } from '@/store/recent-pages';
import { studentsApi, type StudentSummary } from '@/api/students';
import { hrApi, type StaffSummary } from '@/api/hr';
import { extractError } from '@/shared/lib/error-message';

interface PaletteItem {
  key: string;
  group: string;
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  hint?: string;
  /** What happens when the row is selected. Return a Promise to keep the palette open until it resolves. */
  run: () => void | Promise<void>;
}

const QUICK_ACTIONS: Omit<PaletteItem, 'run'>[] = [
  {
    key: 'qa.fee.collect',
    group: 'Quick actions',
    icon: <DollarOutlined />,
    title: 'Collect fee',
    description: 'Find a student and open their fee ledger',
    hint: 'fee',
  },
  {
    key: 'qa.fee.defaulters',
    group: 'Quick actions',
    icon: <WarningOutlined />,
    title: 'Fee defaulters',
    description: 'Outstanding fee report',
    hint: 'fee',
  },
  {
    key: 'qa.student.new',
    group: 'Quick actions',
    icon: <UserOutlined />,
    title: 'New student',
    description: 'Register a new student',
    hint: 'student',
  },
  {
    key: 'qa.staff.new',
    group: 'Quick actions',
    icon: <IdcardOutlined />,
    title: 'New staff',
    description: 'Add a staff member',
    hint: 'hr',
  },
  {
    key: 'qa.payroll',
    group: 'Quick actions',
    icon: <FileTextOutlined />,
    title: 'Payroll runs',
    hint: 'hr',
  },
  {
    key: 'qa.dashboard',
    group: 'Quick actions',
    icon: <DashboardOutlined />,
    title: 'Open dashboard',
    hint: 'home',
  },
  {
    key: 'qa.attendance',
    group: 'Quick actions',
    icon: <TeamOutlined />,
    title: 'Daily attendance',
    hint: 'attendance',
  },
  {
    key: 'qa.exam.schedule',
    group: 'Quick actions',
    icon: <BookOutlined />,
    title: 'Exam schedule',
    hint: 'academics',
  },
];

export function CommandPalette() {
  const isOpen = useCommandPaletteStore((s) => s.isOpen);
  const close = useCommandPaletteStore((s) => s.close);
  const navigate = useNavigate();
  const yearId = useAuthStore((s) => s.academicYearId);
  const { message } = App.useApp();
  const { token } = antdTheme.useToken();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setDebounced('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const hasQuery = debounced.length >= 2;

  const studentsQ = useQuery({
    queryKey: ['palette', 'students', debounced, yearId],
    queryFn: () =>
      studentsApi.search({
        q: debounced,
        academicYearId: yearId ?? undefined,
        status: 'ACTIVE',
        page: 0,
        size: 6,
      }),
    enabled: isOpen && hasQuery,
    staleTime: 30_000,
  });

  const staffQ = useQuery({
    queryKey: ['palette', 'staff', debounced],
    queryFn: () => hrApi.searchStaff({ q: debounced, active: true, size: 6 }),
    enabled: isOpen && hasQuery,
    staleTime: 30_000,
  });

  const collectFeeForStudent = async (row: StudentSummary) => {
    setNavigating(true);
    try {
      const detail = await studentsApi.get(row.id);
      if (!detail.currentEnrollment) {
        message.warning(`${row.firstName} has no active enrollment — enroll them first.`);
        return;
      }
      navigate(`/fee/cashier/${detail.currentEnrollment.id}`);
      close();
    } catch (e) {
      message.error(extractError(e));
    } finally {
      setNavigating(false);
    }
  };

  const quickActions: PaletteItem[] = useMemo(
    () =>
      QUICK_ACTIONS.map((a) => {
        let run: PaletteItem['run'];
        switch (a.key) {
          case 'qa.fee.collect':
            run = () => {
              navigate('/fee/collect');
              close();
            };
            break;
          case 'qa.fee.defaulters':
            run = () => {
              navigate('/fee/defaulters');
              close();
            };
            break;
          case 'qa.student.new':
            run = () => {
              navigate('/students/new');
              close();
            };
            break;
          case 'qa.staff.new':
            run = () => {
              navigate('/hr/staff/new');
              close();
            };
            break;
          case 'qa.payroll':
            run = () => {
              navigate('/hr/payroll');
              close();
            };
            break;
          case 'qa.dashboard':
            run = () => {
              navigate('/dashboard');
              close();
            };
            break;
          case 'qa.attendance':
            run = () => {
              navigate('/attendance/daily');
              close();
            };
            break;
          case 'qa.exam.schedule':
            run = () => {
              navigate('/academics/exam-schedule');
              close();
            };
            break;
          default:
            run = () => close();
        }
        return { ...a, run };
      }),
    [navigate, close],
  );

  const recentPages = useRecentPagesStore((s) => s.items);

  const items: PaletteItem[] = useMemo(() => {
    const out: PaletteItem[] = [];
    const q = debounced;

    // Empty-query view: surface the user's recently-visited pages so the
    // palette is useful before they type anything. Hidden once a search starts.
    if (q.length < 2 && recentPages.length > 0) {
      for (const r of recentPages) {
        out.push({
          key: `recent-${r.pathname}`,
          group: 'Recent',
          icon: <ClockCircleOutlined />,
          title: r.label,
          description: r.pathname,
          hint: 'Open',
          run: () => {
            navigate(r.pathname);
            close();
          },
        });
      }
    }

    if (q.length >= 2) {
      // Students — two rows per match: "Open profile" and "Collect fee"
      const students = studentsQ.data?.content ?? [];
      for (const s of students) {
        const subtitle = [s.currentClassName, s.currentSectionName].filter(Boolean).join(' · ') || 'Not enrolled';
        out.push({
          key: `student-open-${s.id}`,
          group: 'Students',
          icon: <UserOutlined />,
          title: (
            <span>
              {s.firstName} {s.lastName}{' '}
              <Typography.Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {s.registrationNumber}
              </Typography.Text>
            </span>
          ),
          description: subtitle,
          hint: 'Open profile',
          run: () => {
            navigate(`/students/${s.id}`);
            close();
          },
        });
        out.push({
          key: `student-fee-${s.id}`,
          group: 'Collect fee',
          icon: <DollarOutlined />,
          title: (
            <span>
              Collect fee · {s.firstName} {s.lastName}
            </span>
          ),
          description: subtitle,
          hint: 'Open fee ledger',
          run: () => collectFeeForStudent(s),
        });
      }

      // Staff
      const staff = staffQ.data?.rows ?? [];
      for (const st of staff as StaffSummary[]) {
        out.push({
          key: `staff-${st.id}`,
          group: 'Staff',
          icon: <IdcardOutlined />,
          title: `${st.firstName} ${st.lastName}`,
          description:
            [st.designationName, st.departmentName].filter(Boolean).join(' · ') ||
            (st.phone ?? '—'),
          hint: 'Open profile',
          run: () => {
            navigate(`/hr/staff/${st.id}`);
            close();
          },
        });
      }
    }

    // Quick actions — filtered by substring match against title + hint when there's a query
    const lowQuery = q;
    const filteredActions = lowQuery
      ? quickActions.filter((a) => {
          const t = typeof a.title === 'string' ? a.title.toLowerCase() : '';
          return t.includes(lowQuery) || (a.hint ?? '').toLowerCase().includes(lowQuery);
        })
      : quickActions;

    out.push(...filteredActions);

    return out;
  }, [debounced, studentsQ.data, staffQ.data, quickActions, navigate, close, recentPages]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced, items.length]);

  const isLoading = hasQuery && (studentsQ.isFetching || staffQ.isFetching);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[activeIndex];
      if (it && !navigating) void it.run();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  // Group items by their `group` field, preserving the order in which they first appear.
  const grouped = useMemo(() => {
    const groups = new Map<string, PaletteItem[]>();
    for (const it of items) {
      if (!groups.has(it.group)) groups.set(it.group, []);
      groups.get(it.group)!.push(it);
    }
    return Array.from(groups, ([group, rows]) => ({ group, rows }));
  }, [items]);

  // Build a flat index → item map so keyboard nav + group rendering stay in sync.
  const flatItemAt = (idx: number): PaletteItem | undefined => items[idx];

  const itemRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    const it = flatItemAt(activeIndex);
    if (!it) return;
    const el = itemRowRefs.current[it.key];
    el?.scrollIntoView({ block: 'nearest' });
    // We intentionally don't run cleanup; effect re-runs on next activeIndex change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, items]);

  return (
    <Modal
      open={isOpen}
      onCancel={close}
      footer={null}
      destroyOnHidden
      width={640}
      closable={false}
      styles={{ body: { padding: 0 } }}
    >
      <div onKeyDown={onKeyDown}>
        <div style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <Input
            size="large"
            autoFocus
            variant="borderless"
            prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
            suffix={
              isLoading ? <Spin size="small" /> : <kbd className="palette-kbd">esc</kbd>
            }
            placeholder="Search students, fees, staff, or jump to a page…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '14px 16px' }}
          />
        </div>

        <div style={{ maxHeight: 460, overflowY: 'auto', padding: '6px 0' }}>
          {items.length === 0 ? (
            <div style={{ padding: 28 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={hasQuery ? 'No matches' : 'Type to search…'}
              />
            </div>
          ) : (
            grouped.map(({ group, rows }) => (
              <div key={group}>
                <Typography.Text
                  type="secondary"
                  style={{
                    display: 'block',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    padding: '8px 16px 4px',
                  }}
                >
                  {group}
                </Typography.Text>
                {rows.map((row) => {
                  const flatIndex = items.indexOf(row);
                  const isActive = flatIndex === activeIndex;
                  return (
                    <div
                      key={row.key}
                      ref={(el) => {
                        itemRowRefs.current[row.key] = el;
                      }}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onClick={() => {
                        if (!navigating) void row.run();
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 16px',
                        cursor: 'pointer',
                        background: isActive ? token.colorFillTertiary : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 6,
                          background: token.colorFillQuaternary,
                          color: token.colorTextSecondary,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {row.icon}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.title}
                        </div>
                        {row.description && (
                          <Typography.Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.description}
                          </Typography.Text>
                        )}
                      </div>
                      {row.hint && isActive && (
                        <Tag
                          icon={<ArrowRightOutlined />}
                          color="processing"
                          style={{ fontSize: 11 }}
                        >
                          {row.hint}
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: '8px 16px',
            fontSize: 11,
            color: token.colorTextTertiary,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillQuaternary,
          }}
        >
          <span>
            <kbd className="palette-kbd">↑</kbd> <kbd className="palette-kbd">↓</kbd> navigate
          </span>
          <span>
            <kbd className="palette-kbd">↵</kbd> select
          </span>
          <span>
            <kbd className="palette-kbd">esc</kbd> close
          </span>
        </div>
      </div>
    </Modal>
  );
}
